import React from "react";

// US phone formatter: digits → "(xxx) xxx-xxxx". Strips a leading "1"
// country code if present (so "+1 555 123 4567" — pasted OR typed char-by-
// char — still formats to "(555) 123-4567").
export function formatUSPhone(value) {
  if (!value) return "";
  let digits = value.replace(/\D/g, "");
  // Drop a country-code "1" both for the canonical 11-digit case AND for
  // mid-typing — once we already have 10 digits AND the user starts a
  // fresh "1", we treat it as a country-code prefix so the visible field
  // doesn't drift to (1xx) xxx-xxxx.
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  } else {
    digits = digits.slice(0, 10);
  }
  const len = digits.length;
  if (len === 0) return "";
  if (len < 4) return `(${digits}`;
  if (len < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

/**
 * US-formatted phone input. Single text field; auto-formats as user types.
 *
 * Props:
 *  - value: current formatted string
 *  - onChange(value)  // backwards-compat: also tolerates ({phone, phoneType}) signature
 *  - phone, phoneType, testId, required
 */
export default function PhoneInput({ phone, value, onChange, testId = "phone", required, ...rest }) {
  // Support both the new flat `value` prop and the legacy `{phone, phoneType}` shape.
  // Always run the incoming value through the US formatter so raw-digit phones
  // pulled from the API (e.g. "5551234567" or "+15551234567") render as
  // "(555) 123-4567" on screen.
  const raw = value !== undefined ? value : (phone || "");
  const current = formatUSPhone(raw);

  function handleChange(e) {
    const formatted = formatUSPhone(e.target.value);
    if (typeof onChange === "function") {
      // Forward both shapes — pages still using {phone, phoneType} keep working
      onChange.length === 1 ? onChange(formatted) : onChange({ phone: formatted, phoneType: "cell" });
    }
  }

  return (
    <input
      type="tel"
      inputMode="numeric"
      autoComplete="tel-national"
      value={current}
      onChange={handleChange}
      placeholder="(555) 123-4567"
      required={required}
      maxLength={20}
      data-testid={`${testId}-input`}
      style={{
        width: "100%",
        background: "var(--surface)",
        border: "1px solid #000",
        borderRadius: 4,
        padding: "0.65rem 0.85rem",
        fontSize: 14,
        color: "var(--ink)",
        outline: "none",
      }}
      {...rest}
    />
  );
}
